import { resolveLeadPaymentFilterCategory } from '../services/leadsService'

/** Column keys that only appear in lead exports (used for template filtering). */
export const LEADS_EXCLUSIVE_COLUMN_KEYS = new Set([
  'captured_date',
  'source',
  'event_title',
  'agent_name',
  'agent_email',
  'payment_status',
  'has_account',
  'activities',
  'form_responses',
  'quiz_responses',
  'survey_responses'
])

/** Available export column keys and their display labels */
export const EXPORT_COLUMN_OPTIONS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' },
  { key: 'payment_status', label: 'Payment Status' },
  { key: 'has_account', label: 'Has Account' },
  { key: 'captured_date', label: 'Captured Date' },
  { key: 'source', label: 'Source' },
  { key: 'event_title', label: 'Event Title' },
  { key: 'agent_name', label: 'Agent Name' },
  { key: 'agent_email', label: 'Agent Email' },
  { key: 'activities', label: 'Activities / Notes' },
  { key: 'form_responses', label: 'Form Responses' },
  { key: 'quiz_responses', label: 'Quiz Responses' },
  { key: 'survey_responses', label: 'Survey Responses' }
]

/**
 * @param {{ columns?: string[] }} template
 */
export const isLeadExportTemplate = (template) => {
  const columns = Array.isArray(template?.columns) ? template.columns : []
  return columns.some((key) => LEADS_EXCLUSIVE_COLUMN_KEYS.has(key))
}

const humanizeStatus = (status) => {
  if (status === '1_1_completed') return '1-1 Completed'
  if (!status) return 'New'
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
}

const resolveLeadEventPrice = (lead, eventsList) => {
  const eventId = lead.eventId ?? lead.event_id
  const fromDashboard = eventsList.find((e) => e.id === eventId)
  if (fromDashboard && fromDashboard.price != null) {
    return Number(fromDashboard.price)
  }
  if (lead.eventPrice != null) return Number(lead.eventPrice)
  return null
}

const formatPaymentStatus = (lead, eventsList) => {
  const paymentStatus = (lead.paymentStatus ?? lead.payment_status ?? 'pending').toLowerCase()
  if (paymentStatus === 'paid') {
    const amount = lead.paymentAmount ?? lead.payment_amount
    return amount != null ? `Paid $${Number(amount).toFixed(2)}` : 'Paid'
  }
  if (paymentStatus === 'refunded') return 'Refunded'
  if (paymentStatus === 'failed') return 'Failed'

  const price = resolveLeadEventPrice(lead, eventsList)
  if (price !== null && price <= 0) return 'Free'

  const category = resolveLeadPaymentFilterCategory(lead, eventsList)
  if (category === 'paid') return 'Paid'
  if (category === 'free') return 'Free'
  return 'Pending Payment'
}

const formatAnswer = (answer) => {
  if (!answer) return '—'
  if (answer.answer_text) return answer.answer_text
  const opt = answer.marketing_form_options
  if (opt?.option_text_es) return opt.option_text_es
  if (opt?.option_text_en) return opt.option_text_en
  return '—'
}

const formatActivityLog = (activities = []) => {
  if (!Array.isArray(activities) || activities.length === 0) return ''

  return activities
    .map((activity) => {
      const dateValue = activity.activity_date ?? activity.created_at
      const dateStr = dateValue ? new Date(dateValue).toLocaleDateString() : '—'
      const type = activity.activity_type ?? 'activity'
      const notes = (activity.notes ?? '').trim() || '—'
      return `${dateStr} | ${type} | ${notes}`
    })
    .join('\n')
}

const formatFormResponsesByType = (responses = [], formType) => {
  if (!Array.isArray(responses) || responses.length === 0) return ''

  const lines = []

  for (const response of responses) {
    const form = response.marketing_forms
    if (!form || form.type !== formType) continue

    const formTitle = form.title ?? 'Form'
    const answers = response.marketing_form_answers ?? []

    for (const answer of answers) {
      const question =
        answer.marketing_form_questions?.question_text_es ||
        answer.marketing_form_questions?.question_text_en ||
        'Question'
      lines.push(`${formTitle}: ${question} → ${formatAnswer(answer)}`)
    }
  }

  return lines.join('\n')
}

/**
 * Map a single lead to a flat record for export (all possible keys).
 * @param {object} lead
 * @param {{ activitiesByLeadId?: Record<string, object[]>, formResponsesByLeadId?: Record<string, object[]>, agentsById?: Record<string, { name: string, email: string }> }} enrichment
 * @param {Array<{ id: string, title?: string, price?: number }>} eventsList
 * @returns {Record<string, string>}
 */
export function getLeadExportRow(lead, enrichment = {}, eventsList = []) {
  const leadId = lead.id
  const agentId = lead.agentId ?? lead.agent_id
  const agent = agentId ? enrichment.agentsById?.[agentId] : null

  const capturedAt = lead.createdAt ?? lead.created_at ?? lead.registeredAt ?? lead.registered_at
  const captured_date = capturedAt ? new Date(capturedAt).toLocaleDateString() : ''

  const slug = lead.sharingLinkSlug ?? lead.sharing_link_slug
  const source = slug != null && slug !== '' ? String(slug) : 'Direct'

  const eventId = lead.eventId ?? lead.event_id
  const eventFromList = eventsList.find((e) => e.id === eventId)
  const event_title =
    lead.eventTitle ?? lead.events?.title ?? eventFromList?.title ?? ''

  const accountCreated = Boolean(lead.accountCreated ?? lead.account_created)
  const has_account = accountCreated ? 'Account Created' : 'No Account'

  const activities = formatActivityLog(enrichment.activitiesByLeadId?.[leadId] ?? [])
  const formResponses = enrichment.formResponsesByLeadId?.[leadId] ?? []

  return {
    name: lead.fullName ?? lead.full_name ?? lead.name ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    status: humanizeStatus(lead.status),
    payment_status: formatPaymentStatus(lead, eventsList),
    has_account,
    captured_date,
    source,
    event_title,
    agent_name: agent?.name ?? '',
    agent_email: agent?.email ?? '',
    activities,
    form_responses: formatFormResponsesByType(formResponses, 'form'),
    quiz_responses: formatFormResponsesByType(formResponses, 'quiz'),
    survey_responses: formatFormResponsesByType(formResponses, 'survey')
  }
}

/**
 * Build array of export rows (objects with selected columns + custom field keys).
 * @param {object[]} leads
 * @param {string[]} columnKeys
 * @param {Array<{ label: string, value?: string }>} customFields
 * @param {{ activitiesByLeadId?: Record<string, object[]>, formResponsesByLeadId?: Record<string, object[]>, agentsById?: Record<string, { name: string, email: string }> }} enrichment
 * @param {Array<{ id: string, title?: string, price?: number }>} eventsList
 * @returns {Record<string, string>[]}
 */
export function buildExportData(
  leads,
  columnKeys,
  customFields = [],
  enrichment = {},
  eventsList = []
) {
  const keys = [...(columnKeys || [])]
  const customLabels = (customFields || []).map(
    (f) => (f && f.label ? String(f.label).trim() : '').replace(/,/g, ' ') || 'Custom'
  )
  const customValues = (customFields || []).map((f) =>
    f && f.value != null ? String(f.value) : ''
  )

  return leads.map((lead) => {
    const row = getLeadExportRow(lead, enrichment, eventsList)
    const out = {}
    keys.forEach((k) => {
      if (row[k] !== undefined) out[k] = row[k]
    })
    customLabels.forEach((label, i) => {
      if (label) out[label] = customValues[i] ?? ''
    })
    return out
  })
}

/**
 * Escape a CSV cell (quotes and wrap in quotes if needed).
 * @param {string} cell
 * @returns {string}
 */
function escapeCsvCell(cell) {
  const s = String(cell ?? '')
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Download export as CSV.
 * @param {Record<string, string>[]} rows
 * @param {string} filename
 */
export function downloadCSV(rows, filename = 'leads.csv') {
  if (rows.length === 0) {
    const line = [].map(escapeCsvCell).join(',')
    const blob = new Blob([line + '\n'], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    return
  }

  const headers = Object.keys(rows[0])
  const lines = [headers.map(escapeCsvCell).join(',')]
  rows.forEach((row) => {
    lines.push(headers.map((h) => escapeCsvCell(row[h])).join(','))
  })
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Download export as XLSX.
 * @param {Record<string, string>[]} rows
 * @param {string} filename
 */
export async function downloadXLSX(rows, filename = 'leads.xlsx') {
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Leads')
  XLSX.writeFile(wb, filename)
}

/**
 * Download export as PDF (landscape table).
 * @param {Record<string, string>[]} rows
 * @param {string} filename
 * @param {string} [title]
 */
export async function downloadPDF(rows, filename = 'leads.pdf', title = 'Lead Pipeline Export') {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const margin = 40
  let y = 30
  const fontSize = 10
  doc.setFontSize(14)
  doc.text(title, margin, y)
  y += 24

  if (rows.length === 0) {
    doc.setFontSize(fontSize)
    doc.text('No leads to display.', margin, y)
    doc.save(filename)
    return
  }

  const headers = Object.keys(rows[0])
  const colCount = headers.length
  const colWidth = Math.max(60, (pageW - margin * 2) / colCount)
  const rowHeight = 18

  doc.setFontSize(fontSize)
  doc.setFont(undefined, 'bold')
  headers.forEach((h, i) => {
    doc.text(String(h).slice(0, 20), margin + i * colWidth, y)
  })
  y += rowHeight
  doc.setFont(undefined, 'normal')

  const maxY = doc.internal.pageSize.getHeight() - 40
  rows.forEach((row) => {
    if (y > maxY) {
      doc.addPage([doc.internal.pageSize.getHeight(), pageW], 'landscape')
      y = 30
    }
    headers.forEach((h, i) => {
      doc.text(String(row[h] ?? '').slice(0, 24), margin + i * colWidth, y)
    })
    y += rowHeight
  })

  doc.save(filename)
}
